import React, { useEffect, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { FileText, Pencil, Plus, Search, Truck, X } from "lucide-react";
import api, { openServerPdf } from "../../lib/api";
import { Card, Spinner, inputCls } from "../../components/ui-kit";
import { formatItalianDate } from "../../lib/dateUtils";

// Elenco dei DDT emessi. La ricerca (anche per lotto) risponde alla domanda
// "a chi ho consegnato questo lotto?": la rintracciabilità un passo avanti.
export default function DdtList() {
  const { ctx } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("cerca") || "");
  const [rows, setRows] = useState(null);
  const canCreate = ctx.user?.role !== "ispettore";
  const siteName = Object.fromEntries((ctx.sites || []).map((s) => [s.id, s.name]));

  useEffect(() => {
    const handle = setTimeout(() => {
      api.get("/app/ddt", { params: { q: search.trim() || undefined } })
        .then(({ data }) => setRows(data.ddt || [])).catch(() => setRows([]));
      setSearchParams(search.trim() ? { cerca: search.trim() } : {}, { replace: true });
    }, 250);
    return () => clearTimeout(handle);
  }, [search, setSearchParams]);

  return <div className="space-y-5" data-testid="ddt-list-page">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Ricerca</p>
        <h1 className="font-heading text-3xl font-bold">DDT di spedizione</h1>
        <p className="mt-1 text-sm text-slate-500">Tutti i documenti di trasporto emessi. Cerca un lotto per sapere subito a chi è stato consegnato.</p>
      </div>
      {canCreate && <Link to="/app/ddt/nuovo" className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-navy px-5 text-sm font-semibold text-white hover:bg-navy-600" data-testid="ddt-new"><Plus className="h-4 w-4" /> Crea DDT</Link>}
    </div>

    <Card className="p-4">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-10 pr-10`}
          placeholder="Cerca per numero, destinatario, prodotto o lotto…" aria-label="Cerca DDT" data-testid="ddt-search" />
        {search && <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:bg-slate-100" aria-label="Cancella ricerca"><X className="h-4 w-4" /></button>}
      </div>
    </Card>

    {!rows ? <Spinner /> : rows.length === 0 ? (
      <Card className="p-10 text-center text-slate-400"><Truck className="mx-auto mb-3 h-10 w-10 opacity-40" />{search ? "Nessun DDT trovato con questa ricerca." : "Nessun DDT emesso finora."}</Card>
    ) : <div className="space-y-3">
      {rows.map((d) => {
        const cancelled = d.status === "annullato";
        const lots = (d.items || []).filter((i) => i.lot_code);
        return <Card key={d.id} className={`p-4 ${cancelled ? "opacity-70" : ""}`} data-testid={`ddt-row-${d.id}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-heading text-lg font-bold text-navy">DDT n. {d.number_label}</span>
                <span className="text-sm text-slate-500">del {formatItalianDate(d.issue_date)}</span>
                {cancelled && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Annullato</span>}
              </div>
              <div className="mt-1 text-sm font-medium text-slate-800">{d.recipient?.name}</div>
              <div className="text-xs text-slate-500">{[d.causale, siteName[d.site_id] && `da ${siteName[d.site_id]}`].filter(Boolean).join(" · ")}</div>
              <ul className="mt-2 space-y-0.5 text-xs text-slate-600">
                {(d.items || []).slice(0, 4).map((i, idx) => <li key={idx}>{i.description} — {String(i.quantity).replace(".", ",")} {i.unit}{i.lot_code && <> · <span className="whitespace-nowrap">lotto {i.lot_code}</span></>}</li>)}
                {d.items?.length > 4 && <li>… e altri {d.items.length - 4} prodotti</li>}
              </ul>
              {lots.length === 0 && !cancelled && <p className="mt-1 text-xs font-medium text-amber-700">Nessun lotto indicato</p>}
            </div>
            <div className="flex w-full shrink-0 gap-2 sm:w-auto">
              <button type="button" onClick={() => openServerPdf(`/app/ddt/${d.id}/pdf`)} className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-navy px-4 sm:flex-none text-sm font-medium text-navy hover:bg-slate-50" data-testid={`ddt-pdf-${d.id}`}><FileText className="h-4 w-4" /> PDF</button>
              {canCreate && <Link to={`/app/ddt/${d.id}`} className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-hairline px-4 sm:flex-none text-sm font-medium text-slate-700 hover:bg-slate-50"><Pencil className="h-4 w-4" /> {cancelled ? "Apri" : "Modifica"}</Link>}
            </div>
          </div>
        </Card>;
      })}
    </div>}
  </div>;
}
